import React from "react";

import { Trans } from "@lingui/macro";
import PlusIcon from "@uui/Plus";

import { Button, Divider } from "@mui/material";

import HeaderButton from "@app/analytics/components/reports/HeaderButton";
import useAnchorElement, {
  UseAnchorElement,
} from "@app/utils/hooks/useAnchorElement";

import CopyLinkButton from "./CopyLinkButton";
import { ButtonContainer, ResponsiveMenu } from "../styles";
import type { ExternalShareMenuProps, ShareProviderProps } from "../types";
import useExternalShareProviders from "../useExternalShareProviders";

type MenuProps = Omit<ExternalShareMenuProps, "variant"> &
  Pick<UseAnchorElement, "open" | "anchorEl" | "onClose"> & {
    className: string;
    options: ShareProviderProps[];
  };

const Menu = ({
  className,
  open,
  anchorEl,
  onClose,
  options,
  ...optionProps
}: MenuProps) => (
  <ResponsiveMenu
    className={className}
    disableScrollLock
    open={open}
    anchorEl={anchorEl}
    transformOrigin={{ horizontal: "right", vertical: "top" }}
    anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
    onClose={onClose}
  >
    {options.map(({ Component, ...provider }) => (
      <Component
        key={provider.providerId}
        {...optionProps}
        {...provider}
        variant="menu"
      />
    ))}
  </ResponsiveMenu>
);

const ExternalShareNavMenu = (
  props: Omit<ExternalShareMenuProps, "variant">
) => {
  const { open, anchorElement, onClick, onClose } = useAnchorElement();
  const providerOptions = useExternalShareProviders({ variant: "menu" });

  const hasMenuItems = providerOptions.menu.length > 0;

  return (
    <ButtonContainer className={props.inEditMode ? "disabled" : undefined}>
      {providerOptions.nav.map(({ Component, ...provider }) => (
        <Component
          className="gte-md"
          key={provider.providerId}
          {...provider}
          {...props}
          variant="nav"
        />
      ))}
      <CopyLinkButton disabled={props.inEditMode} sessionId={props.sessionId} />
      <Divider
        className={`divider${hasMenuItems ? undefined : " no-options"}`}
        orientation="vertical"
        variant="middle"
        flexItem
      />
      <HeaderButton
        className="gte-md"
        Icon={<PlusIcon />}
        onClick={onClick}
        sx={hasMenuItems ? undefined : { display: "none" }}
      />
      <Button
        variant="text"
        className="lt-md"
        onClick={onClick}
        sx={{
          height: "40px",
          whiteSpace: "nowrap",
          color: "shades.neutral.600",
        }}
      >
        <Trans>Push to...</Trans>
      </Button>
      <Menu
        className="gte-md"
        open={open}
        anchorEl={anchorElement}
        onClose={onClose}
        options={providerOptions.menu}
        {...props}
      />
      <Menu
        className="lt-md"
        open={open}
        anchorEl={anchorElement}
        onClose={onClose}
        options={providerOptions.all}
        {...props}
      />
    </ButtonContainer>
  );
};

export default ExternalShareNavMenu;
